/**
 * useRates — «Ставки» rate-grid backbone (V1 screens_EO/322).
 *
 * EO owns the rate grid (A16); until the EO role ships it is surfaced/edited
 * from the OO ₽-section. Assembles the editable matrix from:
 *   quests                 — matrix columns (active quests)
 *   staff                  — matrix rows (operators)
 *   rates/operators        — operator × quest rates (empty cell = no skill)
 *   rates/dc               — «Ставки ОС» preset (fix + percentages)
 *   finance/config         — global rates (Админ зала ₽/ч, Ставка за отмену)
 *
 * Writes go straight to the existing endpoints; the salary engine consumes
 * them (we never compute pay here — A16). Backend ignores rate ≤ 0 on write,
 * so clearing a cell does not delete it server-side (documented limitation).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../api/client";
import type { ApiList } from "../api/types";

export interface RateQuest {
  id: string;
  shortName: string;
  name: string;
  branchId: string;
}
export interface RateOperator {
  id: string;
  name: string;
}
/** operatorId → questId → rate (only positive rates present). */
export type RateMatrix = Record<string, Record<string, number>>;

export interface OsRates {
  fix_per_game: number;
  pct_upsell: number;
  pct_tea: number;
  pct_mc: number;
  pct_actor: number;
}
export interface GlobalRates {
  hall_admin_hourly_rate: number;
  /** finance_config key `cancel_rate` (assumed; persisted on write). */
  cancel_rate: number;
}

interface RawQuest { id: string; branch_id?: string; name?: string; short_name?: string; active?: number }
interface RawStaff { id: string; name: string; role?: string; status?: string; custom_perms?: string }
interface RawOpRate { operator_id: string; quest_id: string; rate: number }
interface RatesResp { rates?: { results?: RawOpRate[] }; mcRates?: { results?: unknown[] } }
interface DcRateRow { fix_per_game?: number; pct_upsell?: number; pct_tea?: number; pct_mc?: number; pct_actor?: number; branch_id?: string | null }
interface ConfigRow { key: string; value: string }

const EMPTY_OS: OsRates = { fix_per_game: 0, pct_upsell: 0, pct_tea: 0, pct_mc: 0, pct_actor: 0 };
const EMPTY_GLOBALS: GlobalRates = { hall_admin_hourly_rate: 0, cancel_rate: 0 };

function isOperatorRow(s: RawStaff): boolean {
  if (s.status === "fired") return false;
  return s.role === "operator" || s.role === "admin_hall" || (s.custom_perms ?? "").includes("operator");
}

export interface RatesResult {
  quests: RateQuest[];
  operators: RateOperator[];
  matrix: RateMatrix;
  os: OsRates;
  globals: GlobalRates;
  loading: boolean;
  error: string | null;
  saving: boolean;
  reload: () => void;
  saveCell: (operatorId: string, questId: string, rate: number) => Promise<boolean>;
  saveOs: (next: OsRates) => Promise<boolean>;
  saveGlobal: (key: keyof GlobalRates, value: number) => Promise<boolean>;
}

export function useRates(readOnly = false): RatesResult {
  const [quests, setQuests] = useState<RateQuest[]>([]);
  const [operators, setOperators] = useState<RateOperator[]>([]);
  const [matrix, setMatrix] = useState<RateMatrix>({});
  const [os, setOs] = useState<OsRates>(EMPTY_OS);
  const [globals, setGlobals] = useState<GlobalRates>(EMPTY_GLOBALS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [trigger, setTrigger] = useState(0);
  const abortRef = useRef<AbortController | null>(null);
  const reload = useCallback(() => setTrigger((n) => n + 1), []);

  useEffect(() => {
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    setLoading(true);
    setError(null);
    const opt = { signal: ac.signal };

    Promise.all([
      api<ApiList<RawQuest>>("/api/quests", opt),
      api<ApiList<RawStaff>>("/api/staff", opt).catch(() => ({ results: [] as RawStaff[] })),
      api<RatesResp>("/api/rates/operators", opt).catch(() => ({ rates: { results: [] } } as RatesResp)),
      api<ApiList<DcRateRow>>("/api/rates/dc", opt).catch(() => ({ results: [] as DcRateRow[] })),
      api<ApiList<ConfigRow>>("/api/finance/config", opt).catch(() => ({ results: [] as ConfigRow[] })),
    ])
      .then(([qs, st, rt, dc, cfg]) => {
        if (ac.signal.aborted) return;

        setQuests(
          (qs.results ?? [])
            .filter((q) => q.active !== 0)
            .map((q) => ({ id: q.id, shortName: q.short_name || q.name || "—", name: q.name || q.short_name || "—", branchId: q.branch_id ?? "" })),
        );

        setOperators(
          (st.results ?? [])
            .filter(isOperatorRow)
            .map((s) => ({ id: s.id, name: s.name }))
            .sort((a, b) => a.name.localeCompare(b.name, "ru")),
        );

        const m: RateMatrix = {};
        (rt.rates?.results ?? []).forEach((r) => {
          (m[r.operator_id] ??= {})[r.quest_id] = Number(r.rate) || 0;
        });
        setMatrix(m);

        const dcRow = (dc.results ?? [])[0] ?? {};
        setOs({
          fix_per_game: Number(dcRow.fix_per_game) || 0,
          pct_upsell: Number(dcRow.pct_upsell) || 0,
          pct_tea: Number(dcRow.pct_tea) || 0,
          pct_mc: Number(dcRow.pct_mc) || 0,
          pct_actor: Number(dcRow.pct_actor) || 0,
        });

        const cfgMap: Record<string, string> = {};
        (cfg.results ?? []).forEach((r) => { if (r && r.key != null) cfgMap[r.key] = r.value; });
        setGlobals({
          hall_admin_hourly_rate: Number(cfgMap.hall_admin_hourly_rate) || 0,
          cancel_rate: Number(cfgMap.cancel_rate) || 0,
        });

        setLoading(false);
      })
      .catch((e: unknown) => {
        if (ac.signal.aborted) return;
        setError(e instanceof Error ? e.message : "Не удалось загрузить ставки");
        setLoading(false);
      });

    return () => ac.abort();
  }, [trigger]);

  const saveCell = useCallback(async (operatorId: string, questId: string, rate: number): Promise<boolean> => {
    if (readOnly) return false;
    const prev = matrix[operatorId]?.[questId];
    setMatrix((m) => ({ ...m, [operatorId]: { ...(m[operatorId] ?? {}), [questId]: rate } }));
    setSaving(true);
    try {
      await api("/api/rates/operators", { method: "PUT", body: { operator_id: operatorId, rates: { [questId]: rate } } });
      setSaving(false);
      return true;
    } catch (e: unknown) {
      setMatrix((m) => ({ ...m, [operatorId]: { ...(m[operatorId] ?? {}), [questId]: prev ?? 0 } }));
      setError(e instanceof Error ? e.message : "Не удалось сохранить ставку");
      setSaving(false);
      return false;
    }
  }, [matrix, readOnly]);

  const saveOs = useCallback(async (next: OsRates): Promise<boolean> => {
    if (readOnly) return false;
    const prev = os;
    setOs(next);
    setSaving(true);
    try {
      await api("/api/rates/dc", { method: "PUT", body: { ...next, branch_id: null } });
      setSaving(false);
      return true;
    } catch (e: unknown) {
      setOs(prev);
      setError(e instanceof Error ? e.message : "Не удалось сохранить ОС");
      setSaving(false);
      return false;
    }
  }, [os, readOnly]);

  const saveGlobal = useCallback(async (key: keyof GlobalRates, value: number): Promise<boolean> => {
    if (readOnly) return false;
    const prev = globals[key];
    setGlobals((g) => ({ ...g, [key]: value }));
    setSaving(true);
    try {
      await api("/api/finance/config", { method: "POST", body: { [key]: value } });
      setSaving(false);
      return true;
    } catch (e: unknown) {
      setGlobals((g) => ({ ...g, [key]: prev }));
      setError(e instanceof Error ? e.message : "Не удалось сохранить");
      setSaving(false);
      return false;
    }
  }, [globals, readOnly]);

  return { quests, operators, matrix, os, globals, loading, error, saving, reload, saveCell, saveOs, saveGlobal };
}
